/* eslint-disable no-restricted-syntax -- pre-existing styling this change does not touch: the pill's
   padding and the trash glyph's size are off-grid pixel values, so the numeric-spacing and typed-icon
   rules cannot be satisfied without resizing a marker that nine surfaces render. Retune with the
   pins on screen, and convert this file then. */
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import Box from '@mui/material/Box'
import { alpha, useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { AdvancedMarker, AdvancedMarkerAnchorPoint } from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'

import { ReactComponent as TrashCanIcon } from 'assets/map/TrashCanIcon.svg'
import { isCustomerOwned } from 'components/inventory/containers/utils'
import { COMMON } from 'theme/colors'

const ContainerMarkerV2 = React.forwardRef(
  (
    {
      container,
      color = COMMON.black,
      textColor = COMMON.white,
      borderColor = COMMON.white,
      onClick,
      onMouseEnter,
      onMouseLeave,
      zIndex,
      draggable,
      onDragEnd,
      onDelete,
      selected = false,
    },
    ref
  ) => {
    const theme = useTheme()
    const finalBgColor = color || theme.palette.common.black
    const finalTextColor = textColor || theme.palette.common.white
    const finalBorderColor = borderColor || theme.palette.common.white

    const containerNumber = React.useMemo(() => {
      const match = container.containerName?.match(/#(\d+)/)
      return match ? match[1] : container.containerName
    }, [container.containerName])

    const position = React.useMemo(() => {
      const lat = parseFloat(container.latitude)
      const lng = parseFloat(container.longitude)
      return { lat, lng }
    }, [container.latitude, container.longitude])

    const handleClick = useCallback(
      e => {
        if (onClick) onClick(e, container)
      },
      [onClick, container]
    )
    const handleMouseEnter = useCallback(
      e => {
        if (onMouseEnter) onMouseEnter(e, container)
      },
      [onMouseEnter, container]
    )
    const handleMouseLeave = useCallback(
      e => {
        if (onMouseLeave) onMouseLeave(e)
      },
      [onMouseLeave]
    )
    const handleDeleteClick = useCallback(
      e => {
        if (onDelete) {
          e.stopPropagation()
          onDelete(container.id)
        }
      },
      [onDelete, container.id]
    )

    return (
      <AdvancedMarker
        ref={ref}
        anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM}
        draggable={draggable}
        position={position}
        zIndex={selected ? 1000 : zIndex || 10}
        onClick={handleClick}
        onDragEnd={onDragEnd}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            cursor: 'pointer',
            transition: 'z-index 0.15s',
            transformOrigin: 'bottom center',
            '&:hover': {
              transform: 'scale(1.1)',
              zIndex: 999,
            },
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: finalBgColor,
              color: finalTextColor,
              border: `3px solid ${finalBorderColor}`,
              borderRadius: '16px',
              boxShadow: selected
                ? `inset 0 0 0 1.5px ${COMMON.white}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.7)}, 0 2px 4px rgba(0,0,0,0.3)`
                : `inset 0 0 0 1.5px ${COMMON.white}, 0 2px 4px rgba(0,0,0,0.3)`,
              padding: '2px 4px',
              minWidth: '36px',
              height: '28px',
            }}
          >
            {isCustomerOwned(container) && <PersonOutlineOutlinedIcon fontSize="small" sx={{ ml: 0.25, color: 'inherit' }} />}
            <Typography
              sx={{
                fontWeight: 800,
                px: 0.5,
                lineHeight: 1,
              }}
              variant="caption"
            >
              {containerNumber}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px',
                padding: '2px',
                marginLeft: '2px',
                height: '20px',
                width: '20px',
                cursor: onDelete ? 'pointer' : 'inherit',
                '&:hover': onDelete
                  ? {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  : {},
              }}
              onClick={handleDeleteClick}
            >
              <SvgIcon
                inheritViewBox
                component={TrashCanIcon}
                sx={{
                  color: COMMON.white,
                  fontSize: '14px',
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              width: 0,
              height: 0,
              marginTop: '-1px',
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: `8px solid ${finalBorderColor}`,
              zIndex: -1,
            }}
          />
        </Box>
      </AdvancedMarker>
    )
  }
)

ContainerMarkerV2.displayName = 'ContainerMarkerV2'

ContainerMarkerV2.propTypes = {
  container: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    containerName: PropTypes.string,
  }).isRequired,
  color: PropTypes.string,
  textColor: PropTypes.string,
  borderColor: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  zIndex: PropTypes.number,
  draggable: PropTypes.bool,
  onDragEnd: PropTypes.func,
  onDelete: PropTypes.func,
  selected: PropTypes.bool,
}

export default React.memo(ContainerMarkerV2)
